import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, User } from "lucide-react";
import { Reveal, PageHeader } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { useBooking } from "@/context/BookingContext";

export default function Blog() {
  const { content } = useContent();
  const { book } = useBooking();
  const articles = content?.articles || [];
  const [first, ...rest] = articles;

  return (
    <div className="px-4 pb-24 pt-44 sm:px-8 lg:px-16" data-testid="blog-page">
      <PageHeader
        eyebrow="The Skin Journal"
        title="Notes from the Doctor's Desk"
        subtitle="Evidence-based skin, hair and wellness guidance — written by Dr. Javeria Atif, not a marketing team."
        testid="blog-title"
      />

      <div className="mx-auto mt-14 max-w-6xl">
        {first && (
          <Reveal>
            <Link to={`/blog/${first.id}`} data-testid={`blog-featured-${first.id}`} className="group grid overflow-hidden rounded-[2.5rem] bg-[#1A1615] lg:grid-cols-2">
              <div className="overflow-hidden">
                <img src={first.image} alt={first.title} className="h-full w-full object-cover transition-transform duration-700 ease-liquid group-hover:scale-105" />
              </div>
              <div className="flex flex-col justify-center p-8 sm:p-12">
                <div className="flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.2em] text-[#D4AF37]">
                  <span>{first.category}</span>
                  <span className="flex items-center gap-1.5 text-[#A39288]"><Clock className="h-3.5 w-3.5" aria-hidden="true" />{first.read_time}</span>
                </div>
                <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-white sm:text-4xl">{first.title}</h2>
                <p className="mt-4 text-sm font-light leading-relaxed text-[#A39288]">{first.excerpt}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#F3E5AB]">
                  Read the article
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
              </div>
            </Link>
          </Reveal>
        )}

        <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
          {rest.map((a, i) => (
            <Reveal key={a.id} delay={(i % 3) * 0.1}>
              <Link to={`/blog/${a.id}`} data-testid={`blog-card-${a.id}`} className="glass glass-card-hover group block h-full overflow-hidden rounded-[2rem]">
                <div className="overflow-hidden">
                  <img src={a.image} alt={a.title} loading="lazy" className="h-52 w-full object-cover transition-transform duration-700 ease-liquid group-hover:scale-105" />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-4 text-[10px] font-medium uppercase tracking-[0.2em] text-[#B38B2D]">
                    <span>{a.category}</span>
                    <span className="flex items-center gap-1 text-[#A39288]"><Clock className="h-3 w-3" aria-hidden="true" />{a.read_time}</span>
                  </div>
                  <h3 className="mt-3 font-serif text-2xl font-semibold leading-snug text-[#1A1615]">{a.title}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-[#5A504B]">{a.excerpt}</p>
                  <p className="mt-5 flex items-center gap-2 text-xs font-medium text-[#91837C]">
                    <User className="h-3.5 w-3.5 text-[#B38B2D]" aria-hidden="true" /> {a.author}
                  </p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        {articles.length === 0 && (
          <p className="glass rounded-2xl p-8 text-center text-sm font-light text-[#91837C]">Articles are on their way — check back soon.</p>
        )}

        <Reveal className="mt-20">
          <div className="glass rounded-[2.5rem] p-10 text-center sm:p-14" data-testid="journal-cta">
            <p className="eyebrow">Every Week</p>
            <h2 className="mt-4 font-serif text-3xl font-semibold text-[#1A1615] sm:text-4xl">A new essay, every Friday</h2>
            <p className="mx-auto mt-4 max-w-md text-sm font-light text-[#5A504B]">
              Fresh guidance from Dr. Javeria's desk — or skip the reading and get a plan made for your skin.
            </p>
            <button onClick={() => book()} data-testid="journal-book-button" className="btn-gold mt-8 px-8 py-3.5 text-sm">
              Book Your Consultation
            </button>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
